import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { RegistrationFlow, UiText } from '@ory/client';
import { VxButtonComponent } from '@ui/button/button.component';
import { VxFormFieldComponent } from '@ui/form-field/form-field.component';
import { VxInputComponent } from '@ui/input/input.component';
import { AuthRedirectService } from 'src/app/services/authentication/auth-redirect.service';
import {
  csrfToken,
  flowFromError,
  flowMessages,
  oidcProviders,
} from 'src/app/services/authentication/kratos-flow';
import {
  ErrAlreadyLoggedIn,
  ErrBrowserLocationChangeRequired,
  ErrTwoFARequired,
} from 'src/app/services/authentication/kratos.errors';
import {
  Invitation,
  KratosAuthenticationService,
} from 'src/app/services/authentication/kratos.service';
import { PosthogService } from 'src/app/services/posthog/posthog.service';
import { TranslationService } from 'src/app/services/translation/translation.service';
import { environment } from 'src/environments/environment';
import { AuthShellComponent } from '../components/auth-shell/auth-shell.component';
import { KratosMessagesComponent } from 'src/app/core/components/kratos-messages/kratos-messages.component';
import { OidcButtonsComponent } from '../components/oidc-buttons/oidc-buttons.component';

/** History state the code page hands back on "Change it". */
export interface RegisterPageState {
  email?: string;
}

/**
 * Sign-up (the Framer register page): Google, Microsoft or GitHub, or an
 * email address that gets a 6-digit code. Name and password are not asked
 * here any more; onboarding asks for the name, and the code verifies the
 * address. An invitation link prefills and locks the email.
 */
@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  imports: [
    ReactiveFormsModule,
    RouterModule,
    TranslocoModule,
    AuthShellComponent,
    KratosMessagesComponent,
    OidcButtonsComponent,
    VxButtonComponent,
    VxFormFieldComponent,
    VxInputComponent,
  ],
})
export class RegisterPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly auth = inject(KratosAuthenticationService);
  private readonly authRedirect = inject(AuthRedirectService);
  private readonly translation = inject(TranslationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly posthog = inject(PosthogService);

  protected readonly flow = signal<RegistrationFlow | undefined>(undefined);

  protected readonly providers = signal<string[]>([]);

  protected readonly messages = signal<UiText[]>([]);

  protected readonly busy = signal(false);

  protected readonly invitation = signal<Invitation | null>(null);

  protected readonly form = new FormGroup({
    // "Change it" on the code page hands the address back as navigation state
    email: new FormControl(
      (this.router.getCurrentNavigation()?.extras.state as RegisterPageState)
        ?.email ?? '',
      { nonNullable: true, validators: [Validators.required, Validators.email] }
    ),
  });

  ngOnInit(): void {
    this.initSignupAttribution();
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => this.initFlow(params['flow'], params['token']));
  }

  /** Read before anything navigates away from the marketing site's link. */
  private initSignupAttribution(): void {
    this.posthog.registerSignupAttribution(this.route.snapshot.queryParamMap);
  }

  private async initFlow(flowId?: string, inviteToken?: string) {
    if (inviteToken) {
      await this.loadInvitation(inviteToken);
    }
    const create = () =>
      this.auth.createRegistrationFlow(
        `${environment.frontendUrl}/login?check=whoami&action=register`
      );
    try {
      const flow = flowId
        ? await this.auth.getRegistrationFlow(flowId)
        : await create();
      this.useFlow(flow as RegistrationFlow | undefined);
    } catch (error) {
      if (error instanceof ErrAlreadyLoggedIn && (await this.halfSignedIn())) {
        // a login left at the second factor blocks signing up; whoever is
        // here wants a new account, so that login ends
        await this.auth.logout();
        this.useFlow(await create());
      } else if (error instanceof ErrAlreadyLoggedIn) {
        // signed in already: an invitation is joined with this account
        if (inviteToken) {
          this.router.navigate(['/organizations/join'], {
            queryParams: { token: inviteToken },
          });
        } else {
          this.router.navigate(['/']);
        }
      }
    }
  }

  /** The session is a login whose second factor is still due. */
  private async halfSignedIn(): Promise<boolean> {
    try {
      await this.auth.whoami();
      return false;
    } catch (error) {
      return error instanceof ErrTwoFARequired;
    }
  }

  private async loadInvitation(token: string): Promise<void> {
    try {
      const invitation = await new Promise<Invitation>((resolve, reject) =>
        this.auth
          .getInvitation(token)
          .subscribe({ next: resolve, error: reject })
      );
      this.invitation.set(invitation);
      this.form.controls.email.setValue(invitation.email);
      this.form.controls.email.disable();
    } catch {
      // an expired or unknown invitation still lets the person sign up
    }
  }

  private useFlow(flow: RegistrationFlow | undefined): void {
    this.flow.set(flow);
    this.providers.set(flow ? oidcProviders(flow) : []);
    this.messages.set(flow ? flowMessages(flow) : []);
  }

  protected async submit(): Promise<void> {
    const flow = this.flow();
    if (!flow || this.busy()) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.busy.set(true);
    const email = this.form.getRawValue().email.trim();
    try {
      await this.auth.completeRegistrationFlowV2(flow, {
        method: 'code',
        csrf_token: csrfToken(flow),
        traits: { email },
        transient_payload: {
          flow_id: flow.id,
          locale: this.translation.currentLocale,
        },
      });
      // a code sign-up never finishes on the first submit
      this.router.navigate(['/']);
    } catch (error) {
      const next = flowFromError<RegistrationFlow>(error);
      if (next?.state === 'sent_email') {
        this.router.navigate(['/register', 'code'], {
          queryParams: { flow: next.id, invite: this.invitation()?.token },
        });
        return;
      }
      if (next) {
        this.useFlow(next);
      }
      this.busy.set(false);
    }
  }

  protected async signUpWith(provider: string): Promise<void> {
    const flow = this.flow();
    if (!flow || this.busy()) {
      return;
    }
    this.busy.set(true);
    try {
      await this.auth.completeRegistrationFlowV2(flow, {
        method: 'oidc',
        provider,
        csrf_token: csrfToken(flow),
      });
    } catch (error) {
      if (error instanceof ErrBrowserLocationChangeRequired) {
        // stay busy while the browser leaves for the provider: another
        // submit now would invalidate this redirect's OIDC state
        this.authRedirect
          .redirect(error.redirectUri)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.busy.set(false));
        return;
      }
      const next = flowFromError<RegistrationFlow>(error);
      if (next) {
        this.useFlow(next);
      }
      this.busy.set(false);
    }
  }
}
